import { isNil } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { TAX_INFORMATION_FULL_PATH } from "@src/appV2/Accounts/TaxInformation";
import { DeprecatedGlobalAppV1Paths, RootPaths } from "@src/appV2/App/paths";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { SetUpStripeAccountPageContent } from "@src/appV2/redesign/Accounts/PayoutSettings/components/SetUpStripeAccountPageContent";
import { useGoToStripeExpressSignIn } from "@src/appV2/redesign/Accounts/PayoutSettings/hooks/useGoToStripeExpressSignIn";
import { PAYOUT_SETTINGS_FULL_PATH } from "@src/appV2/redesign/Accounts/PayoutSettings/paths";
import { FloatingNavigationButton } from "@src/appV2/redesign/components/FloatingNavigationButton";
import { STRIPE_ONBOARDING_FULL_PATH } from "@src/appV2/redesign/Payments/Onboarding/paths";
import { PAYMENT_DETAILS_FULL_PATH } from "@src/appV2/redesign/Payments/PaymentDetails/paths";
import { PROOF_OF_EARNINGS_FULL_PATH } from "@src/appV2/redesign/Payments/ProofOfEarnings/paths";
import { SHIFT_DISCOVERY_LIST_PATH } from "@src/appV2/redesign/ShiftDiscovery/paths";
import { SHIFT_HISTORY_FULL_PATH } from "@src/appV2/redesign/WorkerShifts/path";
import { useShouldBeOnboardedToStripe } from "@src/appV2/Stripe/useShouldBeOnboardedToStripe";
import { useShouldUseNewStripeOnboarding } from "@src/appV2/Stripe/useShouldUseNewStripeOnboarding";
// eslint-disable-next-line no-restricted-imports -- legacy, use @clipboard-health/date-time. See https://www.notion.so/BP-Date-Time-2ef8643321f48032ad80e8a5b30aa278 for more information
import { format } from "date-fns";
import { last } from "lodash";
import { useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";

import { type PaymentsItemShift, type PaymentsItemWorkplace } from "../api/useGetWorkerPayments";
import { useGetWorkerPayments } from "../api/useGetWorkerPayments";
import { useGetWorkerPaymentsStats } from "../api/useGetWorkerPaymentsStats";
import { type PaymentsListItem } from "../types/PaymentsListItem";
import { EarningsPageHeader } from "./PageHeader";
import { PaymentsList } from "./PaymentsList";

export function EarningsPage() {
  const history = useHistory();
  const shouldUseNewStripeOnboarding = useShouldUseNewStripeOnboarding();
  const shouldOnboardToStripe = useShouldBeOnboardedToStripe();
  const workerPaymentsStats = useGetWorkerPaymentsStats({ enabled: !shouldOnboardToStripe });
  const workerPayments = useGetWorkerPayments({ enabled: !shouldOnboardToStripe });
  const isShiftHistoryPageRedesignEnabled = useCbhFlag(
    CbhFeatureFlag.ENABLE_SHIFT_HISTORY_PAGE_REDESIGN,
    {
      defaultValue: false,
    }
  );

  const { goToStripeExpressSignIn, isGoToStripeExpressSignInLoading } =
    useGoToStripeExpressSignIn();

  const allPayments: Array<PaymentsListItem & { dateGroup: string }> = useMemo(() => {
    if (isNil(workerPayments.data)) {
      return [];
    }

    return workerPayments.data.pages.flatMap((page) => {
      return page.data.map((payment) => {
        const { workplaceId, shiftId, createdAt } = payment.attributes;

        const workplace = page.included.find(
          (entity): entity is PaymentsItemWorkplace =>
            entity.type === "workplace" && entity.id === workplaceId
        );

        const shift = page.included.find(
          (entity): entity is PaymentsItemShift => entity.type === "shift" && entity.id === shiftId
        );

        return {
          ...payment,
          dateGroup: format(new Date(createdAt), "yyyy-MM-dd"),
          workplace,
          shift,
        };
      });
    });
  }, [workerPayments.data]);

  const refetchWorkerPayments = useCallback(async () => {
    await workerPayments.refetch();
  }, [workerPayments]);

  const onGoToPayoutSettings = useCallback(() => {
    history.push(PAYOUT_SETTINGS_FULL_PATH);
  }, [history]);

  const onViewTaxInfo = useCallback(() => {
    history.push(TAX_INFORMATION_FULL_PATH);
  }, [history]);

  const onGoToProofOfEarnings = useCallback(() => {
    history.push(PROOF_OF_EARNINGS_FULL_PATH);
  }, [history]);

  const onFindShiftsClick = useCallback(() => {
    history.push(`${RootPaths.APP_V2_HOME}/${SHIFT_DISCOVERY_LIST_PATH}`);
  }, [history]);

  const onPaymentClick = useCallback(
    (payment: PaymentsListItem) => {
      history.push(PAYMENT_DETAILS_FULL_PATH.replace(":paymentId", payment.id));
    },
    [history]
  );

  useLogEffect(APP_V2_USER_EVENTS.EARNINGS_PAGE_SHOWN, {
    isSetUpStripePageShown: shouldOnboardToStripe,
  });

  return (
    <Stack
      sx={(theme) => ({
        flex: 1,
        backgroundColor: theme.palette.background.tertiary,
        position: "relative",
      })}
    >
      <EarningsPageHeader
        firstPaymentCreatedAt={workerPaymentsStats.data?.data.attributes.firstPaymentCreatedAt}
        totalEarningsInCents={workerPaymentsStats.data?.data.attributes.totalEarningsInCents}
        isLoading={!shouldOnboardToStripe && workerPaymentsStats.isLoading}
        isGoToStripeLoading={isGoToStripeExpressSignInLoading}
        onClickBack={() => history.goBack()}
        onGoToStripe={goToStripeExpressSignIn}
        onViewTaxInfo={onViewTaxInfo}
        onGoToPayoutSettings={onGoToPayoutSettings}
        onGoToProofOfEarnings={onGoToProofOfEarnings}
      />

      {shouldOnboardToStripe && (
        <SetUpStripeAccountPageContent
          stripeOnboardingUrl={
            shouldUseNewStripeOnboarding
              ? STRIPE_ONBOARDING_FULL_PATH
              : DeprecatedGlobalAppV1Paths.PAYMENT_SERVICE_ONBOARDING
          }
          sourceUI={shouldUseNewStripeOnboarding ? "earnings-page" : undefined}
        />
      )}
      {!shouldOnboardToStripe && (
        <PaymentsList
          items={allPayments}
          isLoading={workerPayments.isLoading}
          hasNextPage={last(workerPayments.data?.pages ?? [])?.meta.page.hasMore ?? false}
          refetch={refetchWorkerPayments}
          fetchNextPage={workerPayments.fetchNextPage}
          onPaymentClick={onPaymentClick}
          onFindShiftsClick={onFindShiftsClick}
        />
      )}
      {isShiftHistoryPageRedesignEnabled && !shouldOnboardToStripe && (
        <FloatingNavigationButton
          replace
          label="View shift history"
          href={SHIFT_HISTORY_FULL_PATH}
          onClick={() => {
            logEvent(APP_V2_USER_EVENTS.EARNINGS_PAGE_VIEW_SHIFT_HISTORY_TAPPED);
          }}
        />
      )}
    </Stack>
  );
}
